import { useCallback } from 'react';

import { useSearchParams } from 'react-router-dom';

import {
  ProductFilter,
  StatusFilter,
  SortOption,
  PRODUCT_FILTER_VALUES,
  STATUS_FILTER_VALUES,
  SORT_OPTIONS_VALUES,
  getProductLabel,
  getStatusLabel,
  getSortLabel,
  getProductFilterItems,
  getStatusFilterItems,
  getSortItems,
} from '../utils/filterConfig';

type UseOrdersFiltersResult = {
  // Current filter values
  productFilter: ProductFilter;
  statusFilter: StatusFilter;
  sortBy: SortOption;
  // Labels for display
  productLabel: string;
  statusLabel: string;
  sortLabel: string;
  // Dropdown items
  productItems: ReturnType<typeof getProductFilterItems>;
  statusItems: ReturnType<typeof getStatusFilterItems>;
  sortItems: ReturnType<typeof getSortItems>;
};

const DEFAULT_PRODUCT: ProductFilter = 'all';
const DEFAULT_STATUS: StatusFilter = 'active';
const DEFAULT_SORT: SortOption = 'last_activity_date';

export const useOrdersFilters = (): UseOrdersFiltersResult => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read filters from URL with defaults
  const urlProduct = searchParams.get('product') as ProductFilter | null;
  const urlStatus = searchParams.get('status') as StatusFilter | null;
  const urlSort = searchParams.get('sort') as SortOption | null;

  const productFilter = urlProduct && PRODUCT_FILTER_VALUES.includes(urlProduct) ? urlProduct : DEFAULT_PRODUCT;
  const statusFilter = urlStatus && STATUS_FILTER_VALUES.includes(urlStatus) ? urlStatus : DEFAULT_STATUS;
  const sortBy = urlSort && SORT_OPTIONS_VALUES.includes(urlSort) ? urlSort : DEFAULT_SORT;

  // Generic URL param updater
  const updateSearchParam = useCallback(
    (key: string, value: string, defaultValue: string) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (value === defaultValue) {
          params.delete(key);
        } else {
          params.set(key, value);
        }
        return params;
      });
    },
    [setSearchParams],
  );

  // Setters
  const setProductFilter = useCallback(
    (value: ProductFilter) => updateSearchParam('product', value, DEFAULT_PRODUCT),
    [updateSearchParam],
  );
  const setStatusFilter = useCallback(
    (value: StatusFilter) => updateSearchParam('status', value, DEFAULT_STATUS),
    [updateSearchParam],
  );
  const setSortBy = useCallback(
    (value: SortOption) => updateSearchParam('sort', value, DEFAULT_SORT),
    [updateSearchParam],
  );

  // Generate dropdown items
  const productItems = getProductFilterItems(productFilter, setProductFilter);
  const statusItems = getStatusFilterItems(statusFilter, setStatusFilter);
  const sortItems = getSortItems(sortBy, setSortBy);

  return {
    productFilter,
    statusFilter,
    sortBy,
    productLabel: getProductLabel(productFilter),
    statusLabel: getStatusLabel(statusFilter),
    sortLabel: getSortLabel(sortBy),
    productItems,
    statusItems,
    sortItems,
  };
};
